/**
 * shellProps - the props each part of the shell's render is handed, read off
 * the shell scope `s` (see DeathWatchSimulator).
 *
 * Pure mappings, one per child: which scope name each prop is, including the
 * renames (`onPing` is `handlePing`, `targetId` is `activeTab`, ...). The
 * key order is the old JSX attribute order. Length: each is one prop record
 * (declarative). Extracted from DeathWatchSimulator's render.
 */
import * as multiplayer from '../../multiplayer';
import { actingCombatantId } from '../../engine/turnTracker';
import { isCrowd, isObject } from '../../engine/bodyShape';
import { ownFigureIds } from '../../engine/roster/actedAs';

/** The header: the session bar, the menus and the character library. */
/**
 * What the header's Handouts word counts (v9.8.0): for a player, what is NEW
 * to them; for the GM (and outside a session) everything there is.
 */
function handoutBadge(s) {
  const ctx = s.handoutsContextValue;
  const total = s.handoutsApi.handouts.length;
  if (!ctx || ctx.isGm) return { handoutCount: total, handoutUnread: 0 };
  return { handoutCount: ctx.unread.size, handoutUnread: ctx.unread.size };
}

export const headerProps = (s) => ({
  sessionId: s.sessionId,
  isGm: s.isGm,
  session: s.session,
  user: s.user,
  onlinePlayers: s.onlinePlayers,
  connectedPlayers: s.connectedPlayers,
  canUndo: s.canUndo,
  undoSaving: s.undoSaving,
  isLocked: s.isLocked,
  doUndo: s.doUndo,
  doExport: s.doExport,
  doImport: s.doImport,
  fileInputRef: s.fileInputRef,
  setShowHotkeys: s.setShowHotkeys,
  setShowPermPanel: s.setShowPermPanel,
  setShowSettings: s.setShowSettings,
  setSession: s.setSession,
  onLeave: s.onLeave,
  showCharacterLibrary: s.showCharacterLibrary,
  setShowCharacterLibrary: s.setShowCharacterLibrary,
  activeLibrary: s.activeLibrary,
  // The GM's asset library, for the character library's "From my library" (plan assets-library, stage 2).
  tableAssets: s.tableAssets,
  setEditingCharacter: s.setEditingCharacter,
  setShowCharacterEditor: s.setShowCharacterEditor,
  handleLoadCharacter: s.handleLoadCharacter,
  canSeeRooms: s.canSeeRooms,
  sendToRoom: s.sendToRoom,
  loadCharacters: s.loadCharacters,
  folderFiles: s.folderFiles,
  setFolderFiles: s.setFolderFiles,
  waiting: s.waiting,
  turnTracker: s.turnTracker,
  showDialogAsync: s.showDialogAsync,
  handoutsContextValue: s.handoutsContextValue,
  handoutsDrawer: s.handoutsDrawer,
  ...handoutBadge(s),
  direction: s.direction,
  danger: s.danger,
  roster: s.roster,
  fightSides: s.fightSides,
  startFight: s.startFight,
});

/** The rails' frame: the order's rows for the actor rail's muster, and what opens the panels (the slots are passed beside). */
export const columnsProps = (s) => ({
  railRows: s.railRows,
  // Entering Narrative opens the target panel on Story (2026-10-22, plan retire-classic).
  storyFirst: s.direction?.shown === 'narrative',
  // What the target rail's foot names, and the line of what waits there (2026-10-22, plan retire-classic).
  targetFront: {
    front: s.rightFront?.front ?? 'target',
    calls: s.testCalls?.openCalls?.length ?? 0,
    clocks: s.story?.clocks?.clocks?.length ?? 0,
    rung: s.stealth?.state?.rung ?? null,
  },
});

/** The combat log panel, less its two nodes (the battlefield and the order rail). */
export const logPanelProps = (s, curtain) => ({
  logView: s.logViewPref,
  setLogView: s.setLogView,
  logLanesOff: s.logLanesOff,
  toggleLogLane: s.toggleLogLane,
  logExpandAll: s.logExpandAll,
  toggleLogExpandAll: s.toggleLogExpandAll,
  // The curtain is the layout's (useLogCurtain in ShellMain): its edge, height, collapse and grip.
  logHeight: curtain.height,
  collapsed: curtain.collapsed,
  onToggleCollapsed: curtain.toggleCollapsed,
  logResizeHandlers: curtain.resizeHandlers,
  onLookAt: s.lookAtOnMap,
  narrativeRef: s.narrativeRef,
  mapFold: s.mapFold,
});

/** A finished move's line - and a Pinned crawl's move ends the crawl (2026-10-25). */
const crawlEnding = (s) => {
  // Only while a crawl is open: otherwise the map keeps the shell's own, stable handler (the review).
  if (!s.pinned?.crawl) return s.handleMapMove;
  return (facts) => {
    s.handleMapMove(facts);
    if (facts?.mode === 'crawl') s.pinned.endCrawl();
  };
};

/** ShellBattlefield's `map` record - the BattlefieldPanel props passed through. */
export const battlefieldMap = (s) => {
  const inStealth = s.direction?.frame?.phase === 'stealth';
  return {
    terrain: s.terrain,
    setTerrain: s.setTerrain,
    tokens: s.tokens,
    writeToken: s.writeToken,
    pings: s.pings,
    onPing: s.handlePing,
    onMove: s.handleMapClimb,
    onMoveMade: crawlEnding(s),
    onTokenSettled: s.handleTokenSettled,
    onMoveSettled: s.handleMoveSettled,
    waitingRooms: s.waiting.rooms,
    onActiveRoom: s.setActiveRoomId,
    onDeployRoom: s.deployRoom,
    onDeployMember: s.deployMember,
    onReplaceMember: s.replaceFromDrop,
    onNewRoom: s.newRoom,
    onDismiss: s.dismissFromRoom,
    onDeleteRoom: s.deleteRoom,
    onMoveToRoom: s.moveToRoom,
    onRecall: s.recallToRoom,
    onRecallMany: s.recallManyToRoom,
    focusOn: s.mapFocus,
    playerName: s.displayName,
    roster: s.roster,
    actorId: s.actorId,
    targetId: s.activeTab,
    // Whose turn it is, for the display's camera to follow, and the GM's newest cut (plan map-display, C).
    turnActorId: actingCombatantId(s.turnTracker?.trackerState),
    cameraCut: s.cameraCut?.cut ?? null,
    isGm: s.isGm,
    actorAmmo: s.actorAmmo,
    canApplyDerived: s.battlefieldDerivedValues,
    // How the table carves a hull's arcs: the display's facing ring draws them (plan design-gaps, V1).
    vehicleArcPreset: s.vehicleArcPreset,
    targetName: s.currentTab?.target?.name,
    onApplyMeasurement: s.applyBattlefieldMeasurement,
    liveMeasurement: s.liveMap.measurement,
    coverPinned: s.coverPinned,
    onUnpinCover: s.unpinCover,
    actorMovement: s.actor?.sheet?.movement,
    area: s.previewArea,
    areaEligible: s.previewEligible,
    areaSpawnEligible: s.previewSpawnEligible,
    areaSizeHint: s.previewSizeHint,
    measureLineRef: s.measureLineRef,
    modeRequest: s.mapModeRequest,
    ignoreTerrain: s.ignoreTerrain,
    relationRules: s.relationRules,
    // The take-cover cue (2026-10-25): hidden while positions do not count; a player's own figures show theirs on
    // hover (the GM's are anyone's).
    positionsCount: s.positionsCount,
    coverOwnIds: s.isGm ? null : ownFigureIds(s.actedBy ?? {}, s.actingBy ?? {}, s.voxMe?.uid),
    // The Pinned figure this screen is crawling to cover (useShellPinned), or null.
    crawl: s.pinned?.crawl ?? null,
    onIgnoreTerrainChange: s.onIgnoreTerrainChange,
    library: s.library,
    // The GM's asset library, when their account can host (v11.71.0); null otherwise.
    tableAssets: s.tableAssets,
    // At the table a GM with a library saves a library version too (v11.72.0).
    onSaveMap: s.tableAssets?.saveMap ?? s.saveMap,
    onLoadMap: s.loadMapWithRules,
    onDeleteMap: s.deleteMap,
    onExportMaps: s.exportMaps,
    onImportMaps: s.importMapsFromFile,
    mapFolderFiles: s.mapFolderFiles,
    onMapFolderFiles: s.setMapFolderFiles,
    onImportMapEntries: s.importMapEntries,
    // THE LOG (AND THE RAIL) YIELD WHILE BUILDING AND
    // DEPLOYING (v8.0.1, v8.23.0): the panel reports
    // whether the map needs the column; the shell acts.
    onBuildingChange: s.setMapWantsColumn,
    scenes: s.scenes,
    stage: s.stageSync.stage,
    // A wings scene's set, for its card's thumbnail (plan map-display, I): bound to the session here, where it is.
    readSceneSet: s.sessionId ? (sceneId) => multiplayer.readSceneTerrain(s.sessionId, sceneId) : null,
    stageCard: s.stageSync.card,
    isLocked: s.isLocked,
    deployment: s.deployment,
    onSetDeployment: s.setDeployment,
    // Each team's own arrival point (plan scene-editor-library, B).
    arrivals: s.arrivals,
    onSetArrivals: s.setArrivals,
    avoidOnDeploy: s.avoidOnDeploy,
    redeployOnMapLoad: s.redeployOnMapLoad,
    onSelectTarget: s.setActiveTab,
    hoveredId: s.hoveredId,
    onHoverToken: s.setHoveredId,
    reachMarks: s.reach,
    firingLine: s.firingLine,
    impacts: s.impactsNow,
    walks: s.walksNow,
    // The GM's hidden figures, drawn dashed on the GM's own map (plan map-display, G2); a player holds none.
    hiddenIds: s.isGm ? s.hidden?.hiddenIds : undefined,
    // Where the party last saw what it cannot see (plan map-display, H): the published marks, in Stealth only.
    contactMarks: inStealth ? s.stealth?.state?.contacts?.contacts : undefined,
    // What the opposition sees (plan map-display, H): its watchers, in Stealth only, and the rung's tempo.
    sightWatchers: inStealth ? s.stealth?.watching : undefined,
    stealthRung: s.stealth?.state?.rung,
    inStealth,
    // How far each Signature step carries at this table (plan armoury): the rings and the wedges read it.
    signatureReach: inStealth ? s.signatureReach : undefined,
    // Stealth's mood on the map (Z2): the party leader's own sweep, and each roused watcher's word on its figure.
    stealthLeader: inStealth ? (s.stealth?.state?.approach?.leaderId ?? null) : null,
    oppositionAlerts: inStealth ? s.stealth?.alerts : undefined,
    // The GM's proposed close-in after a sighting (Z2): only the director's sightings hold one.
    closeIn: s.sightings?.proposal ?? null,
    // Whether the actor has taken Gang Up (Z1): the map's threads go solid.
    gangUp: !!s.attackToggles?.gangUp,
    applyConditionModifiers: s.applyConditionModifiers,
    surfaceEffects: s.surfaceEffects,
    sessionId: s.sessionId,
    permissions: s.permissions,
  };
};

/** ShellBattlefield's `derive` record - what it derives props from. */
export const battlefieldDerive = (s) => ({
  activeRoom: s.activeRoom,
  canSeeRooms: s.canSeeRooms,
  setReplaceDialog: s.setReplaceDialog,
  updateCombatantSheet: s.updateCombatantSheet,
  armed: s.currentSlots[s.selectedSlot],
});

/** The order rail's half of `orderRailNode`. */
export const railArgs = (s) => ({
  railRows: s.railRows,
  turnTracker: s.turnTracker,
  isLocked: s.isLocked,
  untrackedRows: s.untrackedRows,
  handleTrackEveryone: s.handleTrackEveryone,
  showDialogAsync: s.showDialogAsync,
  hoveredId: s.hoveredId,
  setHoveredId: s.setHoveredId,
});

/** The turn card's half of `orderRailNode`. */
export const turnCardArgs = (s) => ({
  actor: s.actor,
  intent: s.intent,
  currentSlots: s.currentSlots,
  selectedSlot: s.selectedSlot,
  setSelectedSlot: s.setSelectedSlot,
  handleAttackOrChambered: s.handleAttackOrChambered,
  endSuggested: s.endSuggested,
  activeTab: s.activeTab,
  impactsNow: s.impactsNow,
  ledgerData: s.ledgerData,
});

/** The target panel. */
export const targetPanelProps = (s) => ({
  defencePending: !!(s.isDefencePending && s.isDefencePending(s.activeTab)),
  slot: s.slot,
  fireMode: s.fireMode,
  isMelee: s.isMelee,
  attackMod: s.attackMod,
  rangeBand: s.rangeBand,
  hatred: s.hatred,
  rerollDamage: s.rerollDamage,
  attackerCharStats: s.attackerCharStats,
  // The actor as the roll reads them - conditions and the auras they stand under - for the defence forecast.
  attackerConditions: s.actorProjection ?? null,
  onCycle: s.cycleRail,
});

/** The table's modals: hotkeys, settings, permissions, vox, handouts, the weapon editor. */
export const tableModalProps = (s) => ({
  sessionId: s.sessionId,
  isGm: s.isGm,
  permissions: s.permissions,
  connectedPlayers: s.connectedPlayers,
  showHotkeys: s.showHotkeys,
  setShowHotkeys: s.setShowHotkeys,
  showSettings: s.showSettings,
  setShowSettings: s.setShowSettings,
  showPermPanel: s.showPermPanel,
  setShowPermPanel: s.setShowPermPanel,
  showChannels: s.showChannels,
  setShowChannels: s.setShowChannels,
  viewedHandout: s.viewedHandout,
  handoutPresented: s.handoutPresented,
  closeHandoutView: s.closeHandoutView,
  endHandoutPresent: s.endHandoutPresent,
  handoutFileUrl: s.handoutFileUrl,
  setViewingHandout: s.setViewingHandout,
  chatContextValue: s.chatContextValue,
  vox: {
    channelsApi: s.channelsApi,
    voxPeople: s.voxPeople,
    actingBy: s.actingBy,
  },
  roster: s.roster,
  editingSlot: s.editingSlot,
  setEditingSlot: s.setEditingSlot,
  currentSlots: s.currentSlots,
  handleSaveSlot: s.handleSaveSlot,
  actor: s.actor,
  attackerCharStats: s.attackerCharStats,
  allowCrossTypeSpecials: s.allowCrossTypeSpecials,
  onToggleCrossTypeSpecials: s.onToggleCrossTypeSpecials,
  isLocked: s.isLocked,
  effectPresets: s.effectPresets,
  handleSaveEffectPreset: s.handleSaveEffectPreset,
  // The bench covers the table during play: its bar says whose turn it is and who owes a reaction.
  turnTracker: s.turnTracker,
  reactions: s.reactions,
  currentTarget: currentTargetOf(s),
  // How far each Signature step carries at this table: the bench's plate and Signature section say it.
  signatureReach: s.signatureReach,
  // "My library" on the bench's shelves (plan armoury): the GM's Assets, else null (this browser's).
  armouryShelves: s.armouryShelves ?? null,
});

/** The target the bench's forecast aims at first: the active tab's, by its name. */
function currentTargetOf(s) {
  const tab = Array.isArray(s.tabs) ? s.tabs.find((t) => t.id === s.activeTab) : null;
  return tab?.target ? { name: tab.target.name || tab.label || 'Target', target: tab.target } : null;
}

/** The roster tab's context menu. */
/** Hiding needs somewhere only the GM can read and someone to hide from: the GM's seat, in a session (G1). */
const canHide = (s) => !!(s.isGm && s.sessionId);

export const tabMenuProps = (s) => ({
  tabCtx: s.tabCtx,
  setTabCtx: s.setTabCtx,
  setRenamingTab: s.setRenamingTab,
  actorId: s.actorId,
  setActorId: s.setActorId,
  onDuplicate: s.handleDuplicateTab,
  onClose: s.handleCloseTab,
  turnTracker: s.turnTracker,
  isLocked: s.isLocked,
  // The GM's hide and reveal (plan map-display, G1): offered where hiding means something - the GM, in a session.
  hideReveal: canHide(s) ? s.hideReveal : null,
  // The GM's horde tools (plan map-display, B): split in two, break (it flees). No rule; the GM's hand.
  hordeTools: s.isGm ? { split: s.handleSplitHorde, break: s.handleBreakHorde } : null,
  isHidden: s.hidden?.isHidden,
  // What the party knows of a hidden figure (plan map-display, H): the GM sets its contact level.
  contacts: canHide(s) ? s.contacts : null,
  // The GM's cut (plan map-display, C): every display camera to this figure. A sentence back is a refusal.
  cutTo: canHide(s) ? (id) => s.cameraCut.cutTo(id) : null,
  notify: s.pushToast,
  isCrowdAt: (id) => isCrowd(s.tabs?.find((t) => t.id === id)?.target),
  // An Object is never acted as nor tracked (2026-10-26): the menu offers neither.
  isObjectAt: (id) => isObject(s.tabs?.find((t) => t.id === id)?.target),
});

/**
 * The director's Contacts list in the Stealth column (2026-10-21): the levels, and Revealed - the tab menu's own
 * reveal, behind the same gate. Its refusals are toasted by the verb; anything it throws is reported here.
 */
export const contactsListProps = (s) => {
  if (!canHide(s)) return { contacts: null, onReveal: null };
  const onReveal = (id) =>
    s.hideReveal.reveal(id).catch((err) => {
      console.error('Reveal failed', err);
      s.pushToast?.('The reveal failed - the figure may still be hidden.', { type: 'error', duration: 8000 });
    });
  return { contacts: s.contacts, onReveal };
};

/** The roster's modals: the dialog, the character editor, spawn, replace, export and import. */
export const rosterModalProps = (s) => ({
  canHide: canHide(s),
  isGm: s.isGm,
  updateCombatantSheet: s.updateCombatantSheet,
  libraryTactics: s.libraryTactics,
  libraryBundles: s.libraryBundles,
  dialog: s.dialog,
  handleDialogClose: s.handleDialogClose,
  showCharacterEditor: s.showCharacterEditor,
  setShowCharacterEditor: s.setShowCharacterEditor,
  editingCharacter: s.editingCharacter,
  activeLibrary: s.activeLibrary,
  allowCrossTypeSpecials: s.allowCrossTypeSpecials,
  onToggleCrossTypeSpecials: s.onToggleCrossTypeSpecials,
  isLocked: s.isLocked,
  spawnDialog: s.spawnDialog,
  setSpawnDialog: s.setSpawnDialog,
  handleSpawnConfirm: s.handleSpawnConfirm,
  replaceOutgoing: s.replaceOutgoing,
  setReplaceDialog: s.setReplaceDialog,
  replaceEarlyRefusal: s.replaceEarlyRefusal,
  replaceFromDialog: s.replaceFromDialog,
  waiting: s.waiting,
  showExportDialog: s.showExportDialog,
  roster: s.roster,
  loadouts: s.loadouts,
  terrain: s.terrain,
  library: s.library,
  tokens: s.tokens,
  handoutCount: s.handoutsApi.handouts.length,
  tabs: s.tabs,
  atkProfiles: s.atkProfiles,
  tgtTemplates: s.tgtTemplates,
  handleExportConfirm: s.handleExportConfirm,
  handleExportCancel: s.handleExportCancel,
  importDialogData: s.importDialogData,
  handleImportConfirm: s.handleImportConfirm,
  handleImportCancel: s.handleImportCancel,
});
